import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS,
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_COUNT,
  QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_INFINITE,
  useManagementAddTaskGroup,
  useManagementGetUnitTaskGroupTypes,
  useManagementRetryTaskGroup,
  useManagementUpdateTaskGroup
} from '@apps-common/apis/management/unit-task/task-groups'
import {
  QUERY_MANAGEMENT_GET_UNIT_TASK_GROUP,
  useManagementGetUnitTaskGroup
} from '@apps-common/apis/management/unit-task/task-groups/get-unit-task-group'
import { ButtonRoundedSm, CheckboxInputSM, DatePickerInputSm, InputInfoSM, InputSM, SelectInputSM } from '@apps-common/components/elements'
import { CompanySelectModal, ModalButton, ServerDetailsSelectModal } from '@apps-common/components/modals'
import { ModalBody, ModalCloseButton, ModalContainer, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { CompanyType } from '@apps-common/config/company'
import { commonIcons } from '@apps-common/config/icons'
import { INPUT_DATEPICKER_CLASSES_SM, INPUT_DATEPICKER_CONTAINER_SM } from '@apps-common/config/input-styles'
import { ModalDismiss } from '@apps-common/config/modal'
import { UNIT_TASK_DEVICE_LIMIT_OPTIONS, UnitTaskLimit, UnitTaskType } from '@apps-common/config/unit-task'
import { queryClient } from '@apps-common/lib'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IServerDetailModel } from '@apps-common/types/management/server-details'
import { IManagementUnitSearchParamsModel } from '@apps-common/types/management/unit'
import { ICompanyListModel } from '@apps-common/types/portal/company'
import { IUnitTaskGroupModel } from '@apps-common/types/portal/unit-tasks'

export interface IProgramTaskModifyModalProps {
  taskId?: number
  taskType?: string
  search: IManagementUnitSearchParamsModel
  searchCount: number
  title?: string
  editAction?: 'retry'
}

export const ProgramTaskModifyModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IProgramTaskModifyModalProps
}) => {
  const { taskId, taskType, search, searchCount, editAction } = componentProps
  const isEditing = !!taskId && !Number.isNaN(taskId)

  const [newMc, setNewMc] = useState<ICompanyListModel | null>()
  const [newServer, setNewServer] = useState<IServerDetailModel | null>()
  const [deviceLimit, setDeviceLimit] = useState<number>(UnitTaskLimit.limit50)
  const [enabled, setEnabled] = useState<boolean>(false)

  const { data: taskTypes, isLoading: taskTypesIsLoading, isError: taskTypesIsError } = useManagementGetUnitTaskGroupTypes({ config: {} })

  const {
    data: task,
    isLoading: taskIsLoading,
    isError: taskIsError
  } = useManagementGetUnitTaskGroup({ config: { enabled: isEditing }, id: taskId || 0 })

  useEffect(() => {
    if (task) {
      setEnabled(!!task.runAt)
    }
  }, [task])

  const dismissSuccess = (data: IUnitTaskGroupModel) => {
    queryClient.invalidateQueries({ queryKey: QUERY_MANAGEMENT_GET_UNIT_TASK_GROUP(data?.id) })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_COUNT() })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS() })
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MANAGEMENT_SEARCH_UNIT_TASK_GROUPS_INFINITE() })
    onDismiss(JSON.stringify(data), ModalDismiss.Confirm)
  }

  const { mutate: addTask, isPending: addTaskPending } = useManagementAddTaskGroup(true, (data) => dismissSuccess(data))
  const { mutate: updateTask, isPending: updateTaskPending } = useManagementUpdateTaskGroup(true, (data) => dismissSuccess(data))
  const { mutate: retryTask, isPending: retryTaskPending } = useManagementRetryTaskGroup(true, (data) => dismissSuccess(data))

  const methods = useForm<IUnitTaskGroupModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      taskType: taskType || ''
    }
  })

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors, isSubmitting, isDirty }
  } = methods

  const newMonitoringCentreIdVal = watch('newMonitoringCentreId')
  const newServerKeyVal = watch('serverKey')

  const taskTypeVal = watch('taskType')

  useEffect(() => {
    if (!newMc && !newMonitoringCentreIdVal) return

    setValue('newMonitoringCentreId', newMc?.companyId || '', { shouldValidate: true })
  }, [newMc])

  useEffect(() => {
    if (!newServer && !newServerKeyVal) return

    setValue('serverKey', newServer?.serverKey || '', { shouldValidate: true })
  }, [newServer])

  useEffect(() => {
    if (task && taskTypes) {
      const j = {
        ...task,
        taskType: taskType || task.taskType,
        runAt: undefined
      }

      reset(j)
    }
  }, [task, taskTypes, taskType])

  const onSubmit = async (values: IUnitTaskGroupModel) => {
    if (!isEditing) {
      addTask({
        model: values,
        search: {
          ...search,
          take: deviceLimit
        },
        searchCount
      })
    } else if (editAction === 'retry') {
      retryTask({ model: values })
    } else {
      updateTask({ model: values })
    }
  }

  const getTaskTypeName = () => {
    const type = taskTypes?.find((t) => t.value === taskTypeVal)

    return type?.label || 'task'
  }

  const loading = taskIsLoading || taskIsError || taskTypesIsLoading || taskTypesIsError
  const disabled = loading || isSubmitting || addTaskPending || updateTaskPending || retryTaskPending

  return (
    <ModalContainer innerClasses='!min-h-0 h-auto'>
      <ModalHeaderContainer classes='relative'>
        <div className='flex items-center justify-between'>
          <ModalHeader subtitle='Device Task' title={taskTypeVal?.replaceAll('_', ' ')} titleClasses='capitalize' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>

      <ModalBody classes='!pt-[0px] !pb-[40px]'>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className='flex flex-col gap-[10px]'>
            {!isEditing && !loading && (
              <div className='pt-[20px] pb-[20px]'>
                <div className='px-4 py-2 rounded-xl text-[0.8rem] bg-warning/70 text-on-warning ring-2 ring-warning'>
                  Adding {deviceLimit > searchCount ? searchCount : deviceLimit} of {searchCount} devices for {getTaskTypeName()}.
                </div>
              </div>
            )}
            <div className='grid gap-[20px]'>
              <InputSM
                type='text'
                placeholder='Name the task'
                label='Name'
                id='taskGroupName'
                showLabel
                className='capitalize'
                loading={loading}
                disabled={isEditing || disabled}
                registration={register('taskGroupName', {
                  required: false
                })}
                errors={errors.taskGroupName}
                info='A descriptive name for the task (optional).'
              >
                {isEditing && <commonIcons.editOff className='absolute right-[10px] top-[12px] text-text opacity-40' />}
              </InputSM>
              {taskType === UnitTaskType.monitoringChange && (
                <InputInfoSM
                  id='newMonitoringCentreId'
                  hideNoEditIcon
                  showLabel
                  label='New Monitoring Centre'
                  info='The monitoring centre to which the devices will be transferred.'
                  loading={loading}
                  value={
                    <div>
                      <ModalButton
                        classes='w-full text-text text-left'
                        component={CompanySelectModal}
                        componentProps={{
                          subtitle: 'Selection',
                          title: 'New Monitoring Centre',
                          companyType: CompanyType.MonitoringCentre
                        }}
                        enterAnimation={modalSlideUpEnterAnimation}
                        leaveAnimation={modalSlideUpLeaveAnimation}
                        confirmCallback={(data) => setNewMc(data)}
                        disabled={isEditing || disabled}
                      >
                        {(!newMonitoringCentreIdVal || newMonitoringCentreIdVal?.length === 0) && (
                          <span className='flex items-center justify-between gap-[5px]'>
                            <span className='opacity-30 '>Select Monitoring Centre</span>
                            <span className=' w-[17px] h-[17px] rounded-[7px] action-brand flex items-center justify-center'>
                              <commonIcons.add className='text-[14px]' />
                            </span>
                          </span>
                        )}
                        {newMc && newMonitoringCentreIdVal?.length > 0 && (
                          <div className='flex items-center justify-between gap-[5px] w-full text-text text-left'>
                            {newMc.companyName}
                            <span className=' w-[17px] h-[17px] rounded-[7px] action-brand flex items-center justify-center'>
                              <commonIcons.arrowAngled className='text-[14px]' />
                            </span>
                          </div>
                        )}
                      </ModalButton>
                    </div>
                  }
                  registration={register('newMonitoringCentreId', {
                    required: {
                      value: true,
                      message: 'Required'
                    }
                  })}
                  errors={errors?.newMonitoringCentreId}
                />
              )}
              {taskType === UnitTaskType.serverChange && (
                <InputInfoSM
                  id='serverKey'
                  hideNoEditIcon
                  showLabel
                  label='New server details'
                  info='The server to which the devices will be transferred.'
                  loading={loading}
                  value={
                    <div>
                      {(!newServerKeyVal || newServerKeyVal?.length === 0) && (
                        <ModalButton
                          classes='w-full text-text text-left'
                          component={ServerDetailsSelectModal}
                          componentProps={{
                            subtitle: 'Selection',
                            title: 'New server details'
                          }}
                          enterAnimation={modalSlideUpEnterAnimation}
                          leaveAnimation={modalSlideUpLeaveAnimation}
                          confirmCallback={(data) => setNewServer(data)}
                          disabled={isEditing || disabled}
                        >
                          <span className='flex items-center justify-between gap-[5px]'>
                            <span className='opacity-30 '>Select new server</span>
                            <span className=' w-[14px] h-[14px] rounded-[7px] border-[1px] border-secondary-content flex items-center justify-center'>
                              <commonIcons.add className='text-[10px]' />
                            </span>
                          </span>
                        </ModalButton>
                      )}
                      {newServer && newServerKeyVal?.length > 0 && (
                        <button
                          type='button'
                          className='flex items-center justify-between gap-[5px] w-full border-none text-secondary-content text-left'
                          disabled={isEditing || disabled}
                          onClick={() => setNewServer(null)}
                        >
                          {newServer.name}
                          <commonIcons.closeCircle className='text-[16px]' />
                        </button>
                      )}
                    </div>
                  }
                  registration={register('serverKey', {
                    required: {
                      value: true,
                      message: 'Required'
                    }
                  })}
                  errors={errors?.serverKey}
                  // className={}
                />
              )}
            </div>
            <CheckboxInputSM
              label='Enabled'
              id='enabled'
              loading={loading}
              disabled={disabled || (isEditing && !!task?.runAt)}
              registration={{
                checked: enabled,
                onChange: (e: any) => setEnabled(e.target.checked || false)
              }}
              info='If checked, the the task will run when the set run date/time passes.'
            />
            {enabled && (
              <DatePickerInputSm
                control={control}
                id='runAt'
                name='runAt'
                containerClassName={classNames(INPUT_DATEPICKER_CONTAINER_SM, 'w-full sm:w-auto')}
                className={classNames(INPUT_DATEPICKER_CLASSES_SM)}
                placeholder='Select date/time to run task'
                info='Schedule when the task will run. Tasks can be scheduled up to one month in advance.'
                label='Run at*'
                showLabel
                required
                dateFormat='MMMM d, yyyy h:mm aa'
                showTimeSelect
                minDate={new Date()}
                maxDate={new Date(new Date().setMonth(new Date().getMonth() + 1))}
                // disablePriorTimes
                // disabledPriorTimesHourOffset={1}
                errors={errors.runAt}
                loading={loading}
                disabled={isEditing || disabled}
              />
            )}
            <SelectInputSM
              type='text'
              label='Device limit'
              id='deviceLimit'
              showLabel
              className='capitalize'
              disabled={isEditing || disabled}
              registration={{
                value: deviceLimit.toString(),
                onChange: (e: any) => setDeviceLimit(Number(e.target.value))
              }}
              options={UNIT_TASK_DEVICE_LIMIT_OPTIONS}
            />
            <CheckboxInputSM
              label='Ignore failures'
              id='ignoreFailures'
              loading={loading}
              disabled={disabled}
              registration={register('ignoreFailures')}
              info='If checked, the task will continue to process other devices if one or more devices fail during processing.'
              errors={errors.ignoreFailures}
            />
          </div>
          <ButtonRoundedSm
            type='submit'
            loading={isSubmitting || disabled}
            text={isEditing ? 'Update' : 'Create'}
            icon={<commonIcons.checkCircle className='text-[1.1rem]' />}
            iconRight
            className='w-full md:w-auto action-success mt-6'
          />
        </form>
      </ModalBody>
    </ModalContainer>
  )
}
